import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { Eyebrow } from '@/components/landing/eyebrow'
import { addTicker } from '@/lib/ticker'
import { cn } from '@/lib/utils'

import {
    TOTAL_TIME,
    chapterScrollProgress,
    mapPointProgress
} from '@/lib/three/point-path'

import {
    prefersReducedMotion,
    progressBetween,
    stickyProgress
} from '@/lib/motion'

import { SHOTS } from '@/constants/event'

const LEGEND = [
    { label: 'Serving team', swatch: 'bg-cream' },
    { label: 'Receiving team', swatch: 'bg-coral' },
    { label: 'Ball path', swatch: 'bg-lime' }
]

/** Static top-down court, shown only if the browser cannot draw 3D. */
const CourtDiagram: FC = (): ReactNode => (
    <svg viewBox='0 0 260 520' aria-hidden='true' className='mx-auto h-[86%] w-auto'>
        <rect x='20' y='20' width='220' height='480' fill='#2747d0' stroke='#f5efe2' strokeWidth='3' />
        <rect x='20' y='183' width='220' height='154' fill='#3a63e6' />
        <path d='M20 183 H240 M20 337 H240 M130 20 V183 M130 337 V500' stroke='#f5efe2' strokeWidth='3' />
        <path d='M8 260 H252' stroke='#0d1433' strokeWidth='5' />
        <path d='M180 494 Q 120 260 80 104' stroke='#d6f04a' strokeWidth='3' strokeDasharray='6 6' fill='none' />
    </svg>
)

export const PointSection: FC = (): ReactNode => {
    const trackRef = useRef<HTMLElement>(null)
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const stampRef = useRef<HTMLDivElement>(null)
    const barRefs = useRef<Array<HTMLSpanElement | null>>([])
    const [active, setActive] = useState(0)
    const [fallback, setFallback] = useState(false)

    useEffect(() => {
        let disposed = false
        let cleanup: (() => void) | undefined

        const reduced = prefersReducedMotion()
        const headingFont = getComputedStyle(document.documentElement).getPropertyValue('--font-app-heading').trim() || null

        import('@/lib/three/court-scene')
            .then(({ createCourtScene }) => {
                const canvas = canvasRef.current
                if (disposed || !canvas) return

                let scene: ReturnType<typeof createCourtScene>

                try {
                    scene = createCourtScene(canvas, headingFont)
                }
                catch (error) {
                    console.error('The 3D court could not start, showing the flat diagram instead.', error)
                    setFallback(true)
                    return
                }

                const sync = (): void => scene.resize(canvas.clientWidth, canvas.clientHeight)
                const observer = new ResizeObserver(sync)

                observer.observe(canvas)
                sync()

                let shown = 0
                let glow = 0
                let lastChapter = -1

                const removeTicker = addTicker((clock, delta) => {
                    const track = trackRef.current
                    if (!track) return

                    const rect = track.getBoundingClientRect()
                    const viewportHeight = window.innerHeight
                    if (rect.bottom < 0 || rect.top > viewportHeight) return

                    const progress = stickyProgress(rect, viewportHeight)
                    const { chapter, within, time } = mapPointProgress(progress)

                    shown = reduced ? time : shown + (time - shown) * (1 - Math.exp(-delta * 8))
                    if (Math.abs(time - shown) < 0.0005) shown = time

                    if (chapter !== lastChapter) {
                        lastChapter = chapter
                        setActive(chapter)
                    }

                    barRefs.current.forEach((bar, index) => {
                        if (bar) bar.style.transform = `scaleX(${index < chapter ? 1 : index === chapter ? within : 0})`
                    })

                    const targetGlow = chapter === 2 ? progressBetween(within, 0.35, 0.9) : chapter === 3 ? 1 : 0
                    glow += (targetGlow - glow) * (1 - Math.exp(-delta * 4))

                    if (stampRef.current) {
                        stampRef.current.style.opacity = String(progressBetween(shown, TOTAL_TIME - 0.08, TOTAL_TIME))
                    }

                    scene.render({ time: shown, progress, kitchenGlow: glow, clock, still: reduced })
                })

                cleanup = () => {
                    removeTicker()
                    observer.disconnect()
                    scene.dispose()
                }
            })
            .catch((error) => {
                console.error(error)
                if (!disposed) setFallback(true)
            })

        return () => {
            disposed = true
            cleanup?.()
        }
    }, [])

    const goToChapter = (index: number): void => {
        const track = trackRef.current
        if (!track) return

        const top = track.getBoundingClientRect().top + window.scrollY
        const range = track.offsetHeight - window.innerHeight

        window.scrollTo({
            top: top + range * chapterScrollProgress(index),
            behavior: prefersReducedMotion() ? 'auto' : 'smooth'
        })
    }

    const shot = SHOTS[active]

    return (
        <section
            id='the-point'
            ref={trackRef}
            aria-labelledby='point-title'
            className='relative h-[600vh] bg-ink text-cream'
        >
            <div className='sticky top-0 flex h-svh flex-col overflow-hidden lg:grid lg:grid-cols-12'>
                <div aria-hidden='true' className='px-5 pt-20 lg:hidden'>
                    <p className='font-heading text-[2.1rem] font-black uppercase leading-[0.88]'>
                        Anatomy of a point.
                    </p>

                    <p className='serif-soft font-serif text-lg italic text-lime'>
                        five shots, one kitchen.
                    </p>
                </div>

                <div className='relative order-2 flex flex-col justify-end gap-6 px-5 pb-6 pt-3 lg:order-1 lg:col-span-5 lg:justify-between lg:px-10 lg:pb-12 lg:pt-32'>
                    <div>
                        <Eyebrow className='hidden text-lime lg:flex'>
                            The point
                        </Eyebrow>

                        <h2 id='point-title' className='sr-only font-heading text-[clamp(3rem,5.2vw,5.75rem)] font-black uppercase leading-[0.86] lg:not-sr-only lg:mt-5 lg:block'>
                            Anatomy of a point.
                            <span className='serif-soft mt-1 block font-serif text-[0.6em] font-normal normal-case italic leading-[1.05] text-lime'>
                                five shots, one kitchen.
                            </span>
                        </h2>
                    </div>

                    <div aria-live='polite' className='min-h-[14.5rem] lg:min-h-[17rem]'>
                        <div key={shot.id} className='animate-in fade-in slide-in-from-bottom-3 duration-500 motion-reduce:animate-none'>
                            <p className='font-mono text-xs font-medium tracking-[0.2em] text-lime'>
                                {shot.number}
                                <span className='text-cream/40'> / 0{SHOTS.length}</span>
                            </p>

                            <h3 className='mt-2 font-heading text-[2.6rem] font-extrabold uppercase leading-[0.88] lg:text-6xl'>
                                {shot.name}
                            </h3>

                            <p className='serif-soft mt-1 font-serif text-xl italic text-cream/80 lg:text-2xl'>
                                {shot.accent}
                            </p>

                            <p className='mt-3 max-w-md text-sm leading-relaxed text-cream/75 lg:text-base'>
                                {shot.description}
                            </p>

                            <p className='mt-4 hidden items-center gap-2 rounded-full border border-cream/20 px-3 py-1.5 font-mono text-[10.5px] uppercase tracking-[0.16em] text-cream/80 sm:inline-flex'>
                                <span aria-hidden='true' className='size-1.5 rounded-full bg-lime' />
                                {shot.fact}
                            </p>
                        </div>
                    </div>

                    <ol className='grid grid-cols-5 gap-2'>
                        {SHOTS.map((item, index) => (
                            <li key={item.id}>
                                <button
                                    type='button'
                                    onClick={() => goToChapter(index)}
                                    aria-current={index === active ? 'step' : undefined}
                                    className='group block w-full rounded-sm py-2 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lime'
                                >
                                    <span className='relative block h-1 overflow-hidden rounded-full bg-cream/15'>
                                        <span
                                            ref={(element) => {
                                                barRefs.current[index] = element
                                            }}
                                            className='absolute inset-0 origin-left rounded-full bg-lime'
                                            style={{ transform: 'scaleX(0)' }}
                                        />
                                    </span>

                                    <span
                                        className={cn(
                                            'mt-2 hidden font-mono text-[10px] uppercase tracking-[0.14em] transition-colors group-hover:text-cream lg:block',
                                            index === active ? 'text-lime' : 'text-cream/55'
                                        )}
                                    >
                                        {item.number} {item.short}
                                    </span>

                                    <span className='sr-only'>{`Jump to ${item.name}`}</span>
                                </button>
                            </li>
                        ))}
                    </ol>
                </div>

                <div className='relative order-1 min-h-0 flex-1 lg:order-2 lg:col-span-7'>
                    {fallback
                        ? (
                                <div className='flex h-full items-center justify-center'>
                                    <CourtDiagram />
                                </div>
                            )
                        : (
                                <canvas
                                    ref={canvasRef}
                                    aria-label='A 3D pickleball court where the ball travels through a full point as you scroll'
                                    role='img'
                                    className='absolute inset-0 block h-full w-full'
                                />
                            )}

                    <div
                        ref={stampRef}
                        aria-hidden='true'
                        className='pointer-events-none absolute inset-0 flex items-center justify-center opacity-0'
                    >
                        <p className='-rotate-6 rounded-[2rem] bg-ink/55 px-8 py-4 font-heading text-[18vw] font-black uppercase leading-none text-lime backdrop-blur-sm lg:text-[9vw]'>
                            Point.
                        </p>
                    </div>

                    <ul className='absolute bottom-3 left-5 flex flex-wrap gap-x-4 gap-y-1 lg:bottom-10 lg:left-6'>
                        {LEGEND.map((item) => (
                            <li key={item.label} className='flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.14em] text-cream/70'>
                                <span aria-hidden='true' className={cn('size-2.5 rounded-full', item.swatch)} />
                                {item.label}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </section>
    )
}
